import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Mail, Pause, Play, Trash2, Copy, CheckCircle2 } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function EmailForwardingTab() {
  const { orgId, familyId } = useTenant();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [aliasName, setAliasName] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch email aliases
  const { data: aliases } = useQuery({
    queryKey: ["email-aliases", orgId, familyId],
    queryFn: async () => {
      const query = supabase
        .from("ingestion_email_aliases")
        .select("*")
        .eq("org_id", orgId!);
      
      if (familyId) {
        query.eq("family_id", familyId);
      }

      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  // Create alias mutation
  const createAlias = useMutation({
    mutationFn: async (name: string) => {
      const emailAddress = `${name.toLowerCase()}@usevintage.com`;
      const { data, error } = await supabase
        .from("ingestion_email_aliases")
        .insert({
          org_id: orgId!,
          family_id: familyId || null,
          alias_name: name.toLowerCase(),
          email_address: emailAddress,
          status: "active",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-aliases"] });
      toast({
        title: "Email alias created",
        description: "Your forwarding address is ready to use",
      });
      setIsDialogOpen(false);
      setAliasName("");
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create alias",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle alias status
  const toggleAlias = useMutation({
    mutationFn: async ({ id, currentStatus }: { id: string; currentStatus: string }) => {
      const newStatus = currentStatus === "active" ? "paused" : "active";
      const { error } = await supabase
        .from("ingestion_email_aliases")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-aliases"] });
      toast({
        title: "Alias updated",
        description: "Status changed successfully",
      });
    },
  });

  // Delete alias
  const deleteAlias = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("ingestion_email_aliases")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-aliases"] });
      toast({
        title: "Alias deleted",
        description: "Forwarding address removed",
      });
    },
  });

  const copyToClipboard = (email: string, id: string) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast({
      title: "Copied to clipboard",
      description: email,
    });
  };

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            How Email Forwarding Works
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>1. Create a unique @usevintage.com email address below</p>
          <p>2. Forward documents from your custodians or brokers to this address</p>
          <p>3. Vintage automatically extracts holdings, transactions, and other data</p>
          <p className="text-muted-foreground">
            This works with any email provider and is the simplest way to keep your data updated.
          </p>
        </CardContent>
      </Card>

      {/* Aliases List */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Active Forwarding Addresses</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4" />
              Add Email Alias
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Email Alias</DialogTitle>
              <DialogDescription>
                Choose a unique name for your forwarding address
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="alias-name">Alias Name</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="alias-name"
                    placeholder="familyname"
                    value={aliasName}
                    onChange={(e) => setAliasName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">@usevintage.com</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Use lowercase letters, numbers, and hyphens only
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => createAlias.mutate(aliasName)}
                disabled={!aliasName || createAlias.isPending}
              >
                Create Alias
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {aliases && aliases.length > 0 ? (
        <div className="space-y-3">
          {aliases.map((alias) => (
            <Card key={alias.id}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <code className="px-3 py-1 bg-muted rounded text-sm font-mono">
                        {alias.email_address}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(alias.email_address, alias.id)}
                      >
                        {copiedId === alias.id ? (
                          <CheckCircle2 className="h-4 w-4 text-success" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                      <Badge variant={alias.status === "active" ? "default" : "secondary"}>
                        {alias.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Auto-process: {alias.auto_process ? "On" : "Off"}</span>
                      <span>Created: {new Date(alias.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAlias.mutate({ id: alias.id, currentStatus: alias.status || "active" })}
                    >
                      {alias.status === "active" ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteAlias.mutate(alias.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No forwarding addresses yet</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Create your first @usevintage.com email alias to start forwarding documents
              </p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Email Alias
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
